import { Card, Tag, Empty, Pagination, DatePicker, Button, Select, Tabs, Checkbox, Tooltip, Typography } from "antd";
import { Chart } from "react-chartjs-2";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axios from "axios";
import { FilterOutlined, MailTwoTone, ReloadOutlined, SignatureFilled, StopTwoTone, WarningTwoTone } from "@ant-design/icons";

import FlexCard from "../../components/flex-card";
import AuthenticatedLayout from "../../layouts/authenticated";
import unwrapAxiosPromise from "../../util/unwrapAxiosPromise";
import ActionBar from "../../components/action-bar";
import AssinaturaModal from "./modal";
import { dashboard } from "../../store/assinatura";
import useDispatchWithResult from "../../hooks/useDispatchWithResult";
import BreadcrumbTitulo from "@/components/breadcrumb-titulo";
import CountUp from "react-countup";
import styles from "./_styles.less";

const { Text } = Typography;

const DATE_DISPLAY = "DD/MM/YYYY";
const DATE_PARAM = "YYYY-MM-DD";

export default function AssinaturasPage() {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const dispatch = useDispatchWithResult();
  const [state, setState] = useState({
    items: [],
    total: 0,
    selected: null,
    page: 1,
    search: "",
    status: "TODOS",
    ocrFilter: false,
    pageSize: 8,
    cols: 4,
    activeTab: "RESUMO",
    totals: { pendentes: 15, assinados: 4, cancelados: 0 },
  });

  const [startDate, setStartDate] = useState(moment().startOf("month"));
  const [endDate, setEndDate] = useState(moment().endOf("month"));
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const params = useMemo(
    () => ({
      startDate: startDate?.format(DATE_PARAM),
      endDate: endDate?.format(DATE_PARAM),
      status: state.status !== "TODOS" ? state.status : undefined,
      ocrFilter: state.ocrFilter,
    }),
    [startDate, endDate, state.status, state.ocrFilter]
  );

  const fetchData = useCallback(
    (page = 1, search = "") => {
      unwrapAxiosPromise(axios.get("/assinaturas", { params: { page, pageSize: state.pageSize, search, ...params } })).then((res) => setState((s) => ({ ...s, items: res.items, total: res.total })));
    },
    [state.pageSize, params]
  );

  useEffect(() => {
    if (state.activeTab !== "DOCUMENTOS") return;
    let handle;

    const calc = () => {
      const container = containerRef.current;
      if (!container) return;

      const gap = 7;
      const cardWidth = 290;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const maxCols = 4;
      const estHeight = 120;

      const cols = Math.min(maxCols, Math.max(1, Math.floor((containerWidth + gap) / (cardWidth + gap))));
      const rows = Math.max(1, Math.floor(containerHeight / estHeight));

      setState((s) => ({ ...s, cols, pageSize: cols * rows }));
    };

    calc();

    const onResize = () => {
      clearTimeout(handle);
      handle = setTimeout(calc, 10);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(handle);
    };
  }, [state.activeTab]);

  useEffect(() => {
    unwrapAxiosPromise(
      dispatch(
        dashboard({
          startDate: startDate?.format(DATE_PARAM),
          endDate: endDate?.format(DATE_PARAM),
          status: state.status !== "TODOS" ? state.status : undefined,
          search: state.search,
        })
      )
    ).then((response) => {
      setState((s) => ({ ...s, totals: response }));
    });
  }, [params, state.search]);

  useEffect(() => setState((s) => ({ ...s, page: 1 })), [state.pageSize, state.search, params]);
  useEffect(() => fetchData(state.page, state.search), [fetchData, state.page, state.search, state.pageSize]);

  const renderDatePicker = (value, onChange, placeholder, customStyle = {}) => (
    <DatePicker value={value} onChange={onChange} onOpenChange={(open) => setIsPickerOpen(open)} format={DATE_DISPLAY} allowClear={false} inputReadOnly placeholder={t(placeholder)} style={{ cursor: "pointer", ...customStyle }} />
  );

  return (
    <AuthenticatedLayout breadcrumb={[t("Envelopes")]}>
      <FlexCard
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <BreadcrumbTitulo selected={t("Envelopes")} />
          </div>
        }
        headStyle={{ backgroundColor: "#f0f0f06b" }}
      >
        <Tabs
          activeKey={state.activeTab}
          onChange={(k) => setState((s) => ({ ...s, activeTab: k }))}
          items={[
            { key: "RESUMO", label: t("Resumo") },
            { key: "DOCUMENTOS", label: t("Detalhes") },
          ]}
        />

        {state.activeTab === "DOCUMENTOS" && (
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Tooltip title={t("Filtros")}>
              <FilterOutlined style={{ fontSize: 13 }} />
            </Tooltip>
            <Tooltip title={t("Data de início")}>{renderDatePicker(startDate, setStartDate, "Data inicial", { marginLeft: -5 })}</Tooltip>
            <Tooltip title={t("Data de fim")}>{renderDatePicker(endDate, setEndDate, "Data final")}</Tooltip>
            <Select
              value={state.status}
              onChange={(v) => setState((s) => ({ ...s, status: v || "TODOS", page: 1 }))}
              style={{ minWidth: 180 }}
              options={[
                { label: t("Todos"), value: "TODOS" },
                { label: t("Pendente"), value: "PENDENTE" },
                { label: t("Concluído"), value: "CONCLUIDO" },
                { label: t("Cancelado"), value: "CANCELADO" },
              ]}
            />
            <ActionBar onSearch={(search) => setState((s) => ({ ...s, search, page: 1 }))} searchOnEnterOrClick>
              <Checkbox checked={state.ocrFilter} onChange={() => setState((s) => ({ ...s, ocrFilter: !s.ocrFilter, page: 1 }))} style={{ color: "#999999" }}>
                {t("Pesquisar em conteúdo do arquivo")}
              </Checkbox>
            </ActionBar>
          </div>
        )}

        {state.activeTab === "RESUMO" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            <div
              style={{
                width: "100%",
                height: 400,
                position: "relative",
                pointerEvents: isPickerOpen ? "none" : "auto",
              }}
            >
              <Chart
                type="bar"
                style={{ zIndex: "auto" }}
                data={{
                  labels: (state.totals.Meses || []).map((m) => m.mes),
                  datasets: [
                    {
                      label: "Total Assinaturas",
                      data: (state.totals.Meses || []).map((m) => m.total),
                      backgroundColor: "rgba(54, 162, 235, 0.5)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    title: { display: true, text: "Totais de Assinaturas nos Últimos 12 Meses" },
                  },
                  scales: {
                    y: { beginAtZero: true, precision: 0 },
                  },
                }}
              />
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 15 }}>
              <Card style={{ minWidth: 240, flex: "1 1 240px", borderRadius: 7 }}>
                <div className={styles["header-card-totais"]}>
                  <MailTwoTone style={{ fontSize: 14 }} />
                  <strong>{t("Totais")}</strong>
                </div>
                <CountUp style={{ fontSize: 23, fontWeight: 700, marginLeft: 6 }} end={state.totals.total} duration={2.75} />
              </Card>

              <Card style={{ minWidth: 240, flex: "1 1 240px", borderRadius: 7 }}>
                <div className={styles["header-card-pendentes"]}>
                  <WarningTwoTone twoToneColor="#faad14" style={{ fontSize: 14 }} />
                  <strong>{t("Pendentes")}</strong>
                </div>
                <CountUp style={{ fontSize: 23, fontWeight: 700, marginLeft: 6 }} end={state.totals.pendentes} duration={3} />
              </Card>

              <Card style={{ minWidth: 240, flex: "1 1 240px", borderRadius: 7 }}>
                <div className={styles["header-card-assinados"]}>
                  <SignatureFilled style={{ fontSize: 14, color: "#4abb27" }} />
                  <strong>{t("Assinados")}</strong>
                </div>
                <CountUp style={{ fontSize: 23, fontWeight: 700, marginLeft: 6 }} end={state.totals.assinados} duration={3.2} />
              </Card>

              <Card style={{ minWidth: 240, flex: "1 1 240px", borderRadius: 7 }}>
                <div className={styles["header-card-cancelados"]}>
                  <StopTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 14 }} />
                  <strong>{t("Cancelados")}</strong>
                </div>
                <CountUp style={{ fontSize: 23, fontWeight: 700, marginLeft: 6 }} end={state.totals.cancelados} duration={3.45} />
              </Card>
            </div>

            {!state.items.length ? (
              <div style={{ padding: 40, display: "flex", justifyContent: "center", width: "100%" }}>
                <Empty description={t("Nenhum documento encontrado")} />
              </div>
            ) : (
              <>
                <div ref={containerRef} className={styles["container-cards"]}>
                  {state.items.map((doc) => {
                    const assinadas = doc.assinaturas?.filter((a) => a.assinado).length || 0;
                    const totalAss = doc.assinaturas?.length || 0;
                    const criado = moment(doc.created_at).format(`${DATE_DISPLAY} HH:mm`);
                    const concluido = moment(doc.updated_at).format(`${DATE_DISPLAY} HH:mm`);

                    let statusText = t("Pendente");
                    let statusIcon = <WarningTwoTone twoToneColor="#faad14" />;
                    let statusColor = "orange";
                    if (doc.status === "CANCELADO") {
                      statusText = t("Cancelado");
                      statusColor = "red";
                      statusIcon = <StopTwoTone twoToneColor="#ff4d4f" />;
                    } else if (doc.assinado) {
                      statusText = t("Concluído");
                      statusColor = "green";
                      statusIcon = <SignatureFilled style={{ color: "#4abb27" }} />;
                    }

                    return (
                      <Card
                        className={styles["card-envelope"]}
                        hoverable
                        key={doc.id}
                        title={
                          <div
                            style={{
                              display: "flex",
                              gap: 5,
                              alignItems: "center",
                            }}
                          >
                            <Tooltip title={statusText}>
                              <Tag
                                color={statusColor}
                                style={{
                                  margin: 0,
                                  borderRadius: "50%",
                                  display: "flex",
                                  padding: 5,
                                  flexDirection: "column",
                                }}
                                icon={statusIcon}
                              />
                            </Tooltip>
                            <Text
                              strong
                              ellipsis={{ tooltip: doc.nome }}
                              style={{
                                lineHeight: "1.4",
                                maxHeight: "44px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {doc.nome}
                            </Text>
                          </div>
                        }
                        bodyStyle={{
                          flex: 1,
                          padding: "12px 16px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                        onClick={() => setState((s) => ({ ...s, selected: doc }))}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "12px",
                              color: "rgba(0,0,0,0.65)",
                              lineHeight: "1.4",
                            }}
                          >
                            <div style={{ marginBottom: "4px" }}>
                              <span style={{ fontWeight: "500" }}>{t("Criado em")}:</span> {criado}
                            </div>

                            <div>
                              {doc.status === "CANCELADO" ? (
                                <>
                                  <span style={{ fontWeight: "500" }}>{t("Cancelado em")}:</span> {concluido}
                                </>
                              ) : doc.assinado ? (
                                <>
                                  <span style={{ fontWeight: "500" }}>{t("Concluído em")}:</span> {concluido}
                                </>
                              ) : (
                                <>
                                  <div>
                                    <span style={{ fontWeight: "500" }}>{t("Assinaturas")}:</span> {assinadas}/{totalAss}
                                  </div>
                                  <div style={{ color: "#999", marginTop: 2 }}>
                                    {t("Válido até")} {moment(doc.created_at).add(30, "days").format(`${DATE_DISPLAY} HH:mm`)}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
                <div style={{ marginTop: 15, display: "flex", justifyContent: "flex-end" }}>
                  <Pagination current={state.page} pageSize={state.pageSize} total={state.total} onChange={(page) => setState((s) => ({ ...s, page }))} showSizeChanger={false} />
                </div>
              </>
            )}
          </>
        )}
      </FlexCard>

      <AssinaturaModal
        open={!!state.selected}
        doc={state.selected}
        onClose={(shouldRefresh = false) => {
          setState((s) => ({ ...s, selected: null }));
          if (shouldRefresh) {
            fetchData(state.page, state.search);
          }
        }}
      />
    </AuthenticatedLayout>
  );
}
